import type {PermissionRequirement, PortalModuleKey, StaffModuleKey} from '@briary/shared'

export interface HelpContext {path:string;tab?:string}
export interface HelpTopic {
  id:string; areas:readonly ('admin'|'portal')[]; title:string; category:string; entryPath:string
  contexts:readonly HelpContext[]; requirements:readonly PermissionRequirement[]; steps:readonly string[]
  notice?:string; keywords?:readonly string[]; portalModule?:PortalModuleKey; staffModule?:StaffModuleKey
  portalOnly?:boolean; superadminOnly?:boolean; actionMode?:'editable'|'live'
}

/** Stable IDs also name the reviewed, fictional video/caption asset pair. */
export const HELP_TOPICS:readonly HelpTopic[]=[
  {
    "id": "C01",
    "areas": [
      "admin",
      "portal"
    ],
    "title": "Sign in, finish setup and find your workspace",
    "entryPath": "/account",
    "contexts": [
      {
        "path": "/admin"
      },
      {
        "path": "/portal"
      },
      {
        "path": "/account"
      }
    ],
    "requirements": [],
    "steps": [
      "Sign in and complete any required password or two-step setup.",
      "Use the area menu to switch only between your available workspaces.",
      "Open your avatar menu, then Account & security, to review your account."
    ],
    "category": "Getting started"
  },
  {
    "id": "M01",
    "areas": [
      "portal"
    ],
    "title": "Find your site and plot",
    "entryPath": "/portal/sites",
    "contexts": [
      {
        "path": "/portal/sites"
      },
      {
        "path": "/portal/sites/:id"
      }
    ],
    "requirements": [],
    "steps": [
      "Open Sites & plots.",
      "Find the assigned site.",
      "Open the plot and inspect its progress.",
      "Use the Photos or Documents tab for permitted items."
    ],
    "category": "Your sites",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M02",
    "areas": [
      "portal"
    ],
    "title": "Read photo progress and available documents",
    "entryPath": "/portal/sites",
    "contexts": [
      {
        "path": "/portal/sites/:id",
        "tab": "photos"
      },
      {
        "path": "/portal/sites/:id",
        "tab": "documents"
      }
    ],
    "requirements": [],
    "steps": [
      "Open Photos for the assigned site.",
      "Filter the visible evidence status.",
      "Open the plot drawer.",
      "Use Documents to view only files made available to you."
    ],
    "category": "Your sites",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access. This guide covers viewing existing evidence; it does not describe mobile uploads."
  },
  {
    "id": "M03",
    "areas": [
      "portal"
    ],
    "title": "Request an EPC and follow progress",
    "entryPath": "/portal/epc",
    "contexts": [
      {
        "path": "/portal/epc"
      },
      {
        "path": "/portal/sites/:id",
        "tab": "epc"
      }
    ],
    "requirements": [],
    "steps": [
      "Open EPCs & BRELs and choose the site and plot.",
      "Open the request form and check the building form, heating details and supporting documents.",
      "Review the details before submitting once.",
      "Follow the request status; an EPC request is not an issued certificate."
    ],
    "category": "Your sites",
    "portalModule": "epc",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access. Submitting is available when editing is enabled and may send a confirmation."
  },
  {
    "id": "M04",
    "areas": [
      "portal"
    ],
    "title": "Return a signed BREL and download a certificate",
    "entryPath": "/portal/epc",
    "contexts": [
      {
        "path": "/portal/epc"
      },
      {
        "path": "/portal/sites/:id",
        "tab": "epc"
      }
    ],
    "requirements": [],
    "steps": [
      "Choose the correct site and plot in EPCs & BRELs.",
      "Read the BREL status and open the available document.",
      "Return the signed BREL when the page offers that action.",
      "Download the certificate once it is available."
    ],
    "category": "Your sites",
    "portalModule": "epc",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M05",
    "areas": [
      "portal"
    ],
    "title": "Submit alternative photo evidence",
    "entryPath": "/portal/photos/declarations",
    "contexts": [
      {
        "path": "/portal/photos/declarations"
      },
      {
        "path": "/portal/photos/declarations/:plot"
      }
    ],
    "requirements": [],
    "steps": [
      "Open the plot on your site page and choose Sign declaration.",
      "Select the outstanding areas and choose Prepare statement.",
      "Read the statement, add the reason, signature and consent.",
      "Choose Sign and submit declaration once, then follow the review status."
    ],
    "category": "Your sites",
    "portalModule": "alternativeEvidence",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access.",
    "portalOnly": true
  },
  {
    "id": "M06",
    "areas": [
      "portal"
    ],
    "title": "Request and manage an air test",
    "entryPath": "/portal/air-tests",
    "contexts": [
      {
        "path": "/portal/air-tests"
      },
      {
        "path": "/portal/air-tests/calendar"
      }
    ],
    "requirements": [],
    "steps": [
      "Choose a permitted site.",
      "Enter the booking details.",
      "Follow proposed/confirmed dates.",
      "Use the available accept/change/cancellation action appropriate to its status."
    ],
    "category": "Your sites",
    "portalModule": "airTesting",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M07",
    "areas": [
      "portal"
    ],
    "title": "Submit and track a work request",
    "entryPath": "/portal/requests",
    "contexts": [
      {
        "path": "/portal/requests"
      }
    ],
    "requirements": [],
    "steps": [
      "Open Requested jobs and choose Request work.",
      "Select an assigned Project, or choose New project and enter New project name.",
      "Enter Services requested, any Other service, Comments and optional document links; review these details.",
      "Choose Submit request once and inspect the recorded result."
    ],
    "category": "Your sites",
    "portalModule": "workRequests",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M08",
    "areas": [
      "portal"
    ],
    "title": "Find shared health and safety information",
    "entryPath": "/portal/health-safety",
    "contexts": [
      {
        "path": "/portal/health-safety"
      }
    ],
    "requirements": [],
    "steps": [
      "Open Health & safety.",
      "Find an available shared document.",
      "Read or download it."
    ],
    "category": "Your sites",
    "portalModule": "healthSafety",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M09",
    "areas": [
      "portal"
    ],
    "title": "Send feedback",
    "entryPath": "/portal/feedback",
    "contexts": [
      {
        "path": "/portal/feedback"
      }
    ],
    "requirements": [],
    "steps": [
      "Open Feedback.",
      "Describe the issue with enough context.",
      "Review before sending."
    ],
    "category": "Your sites",
    "portalModule": "feedback",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M10",
    "areas": [
      "portal"
    ],
    "title": "Read site progress metrics",
    "entryPath": "/portal/site-metrics",
    "contexts": [
      {
        "path": "/portal/site-metrics"
      },
      {
        "path": "/portal/site-metrics/:id"
      },
      {
        "path": "/portal/site-metrics/*"
      }
    ],
    "requirements": [],
    "steps": [
      "Choose an assigned site.",
      "Read the progress measures.",
      "Use the same site/plot context when contacting Briary."
    ],
    "category": "Your sites",
    "portalModule": "siteMetrics",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M11",
    "areas": [
      "portal"
    ],
    "title": "Use shared HEM homes",
    "entryPath": "/portal/hem",
    "contexts": [
      {
        "path": "/portal/hem"
      },
      {
        "path": "/portal/hem/:id"
      },
      {
        "path": "/portal/hem/*"
      }
    ],
    "requirements": [],
    "steps": [
      "Open a permitted shared home.",
      "Inspect the current specification.",
      "Create a client version only when the page offers it."
    ],
    "category": "Your sites",
    "portalModule": "hem",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "M12",
    "areas": [
      "portal"
    ],
    "title": "View a shared model",
    "entryPath": "/portal/models",
    "contexts": [
      {
        "path": "/portal/models"
      },
      {
        "path": "/portal/models/:id"
      },
      {
        "path": "/portal/models/*"
      }
    ],
    "requirements": [],
    "steps": [
      "Open Models.",
      "Choose an available assigned-site model.",
      "Use the viewer controls."
    ],
    "category": "Your sites",
    "portalModule": "sketchupModels",
    "notice": "Only your assigned sites and plots are available. A module setting does not add site access."
  },
  {
    "id": "A01",
    "areas": [
      "admin"
    ],
    "title": "Find, filter and sort tasks",
    "entryPath": "/admin/tasks",
    "contexts": [
      {
        "path": "/admin/tasks"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "tasks"
      }
    ],
    "requirements": [
      "tasks.view"
    ],
    "steps": [
      "Open Tasks.",
      "Filter by client, project, person or status.",
      "Click Task, Project, Client, Status, Priority, Due, Assigned or Estimate to sort; click again for direction.",
      "Use the Sort control or next page; the URL retains the current filters."
    ],
    "category": "Tasks",
    "keywords": [
      "sort",
      "filter",
      "due",
      "assignee",
      "client"
    ]
  },
  {
    "id": "A02",
    "areas": [
      "admin"
    ],
    "title": "Update a task and record time",
    "entryPath": "/admin/tasks",
    "contexts": [
      {
        "path": "/admin/tasks/:id"
      }
    ],
    "requirements": [
      "tasks.view"
    ],
    "steps": [
      "Open the task and read its status, dates, assignees and any notes.",
      "Use the available status or progress controls if you own or are assigned to the task.",
      "Add a clear note or issue when needed.",
      "Use the timer or a time entry for work you have actually done."
    ],
    "category": "Tasks",
    "notice": "Record-level access and ownership still apply. Managing assignments and deleting a task require tasks.manage."
  },
  {
    "id": "A03",
    "areas": [
      "admin"
    ],
    "title": "Use the Operations workboard",
    "entryPath": "/admin/operations",
    "contexts": [
      {
        "path": "/admin/operations"
      }
    ],
    "requirements": [
      "tasks.view"
    ],
    "steps": [
      "Open Operations to read the workboard.",
      "Choose the relevant team or workstream.",
      "Inspect capacity and plots in progress.",
      "Use Tasks to open work you are permitted to access."
    ],
    "category": "Tasks",
    "staffModule": "operations",
    "notice": "Operations must be enabled for your account. Setup changes also require task management permission."
  },
  {
    "id": "A04",
    "areas": [
      "admin"
    ],
    "title": "Review photo evidence",
    "entryPath": "/admin/photos",
    "contexts": [
      {
        "path": "/admin/photos"
      },
      {
        "path": "/admin/photos/declarations"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "photos"
      }
    ],
    "requirements": [
      "photos.review"
    ],
    "steps": [
      "Filter the Photo review queue.",
      "Open the exact plot, photograph or report and read the requirements.",
      "Review the evidence before making a decision.",
      "For alternative evidence, open the declaration review and check the submitted statement."
    ],
    "category": "Evidence",
    "notice": "Use the normal review controls. A navigation guide does not replace evidence or regulatory checks."
  },
  {
    "id": "A05",
    "areas": [
      "admin"
    ],
    "title": "Follow an EPC request and its documents",
    "entryPath": "/admin/epc",
    "contexts": [
      {
        "path": "/admin/epc"
      },
      {
        "path": "/admin/epc/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "epc"
      }
    ],
    "requirements": [
      "epc.view"
    ],
    "steps": [
      "Find the request.",
      "Read its stage, requirements and documents.",
      "Inspect the next required staff action.",
      "Open the final document when available."
    ],
    "category": "Evidence",
    "notice": "Managing or lodging an EPC has separate permissions and technical checks. This guide covers navigation only."
  },
  {
    "id": "A06",
    "areas": [
      "admin"
    ],
    "title": "Find projects, companies and contacts",
    "entryPath": "/admin/projects",
    "contexts": [
      {
        "path": "/admin/projects"
      },
      {
        "path": "/admin/companies"
      },
      {
        "path": "/admin/contacts"
      }
    ],
    "requirements": [
      "projects.view"
    ],
    "steps": [
      "Open Projects and search by name, number or client company.",
      "Switch between the project overview and sites and plots as needed.",
      "Open a project and select the relevant tab.",
      "Use Companies or Contacts for company and person details."
    ],
    "category": "Projects"
  },
  {
    "id": "A07",
    "areas": [
      "admin"
    ],
    "title": "Find and follow an enquiry",
    "entryPath": "/admin/enquiries",
    "contexts": [
      {
        "path": "/admin/enquiries"
      },
      {
        "path": "/admin/enquiries/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "enquiries"
      }
    ],
    "requirements": [
      "enquiries.view"
    ],
    "steps": [
      "Open Enquiries and find the relevant request.",
      "Open its detail to read the site, contact, notes and progress.",
      "Review the current requirements before taking the next action.",
      "Only use edit or confirmation controls when your account has the required management permission."
    ],
    "category": "Sales"
  },
  {
    "id": "A08",
    "areas": [
      "admin"
    ],
    "title": "Find and preview a sales invoice",
    "entryPath": "/admin/invoices",
    "contexts": [
      {
        "path": "/admin/invoices"
      },
      {
        "path": "/admin/invoices/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "invoices"
      }
    ],
    "requirements": [
      "invoices.view"
    ],
    "steps": [
      "Open Invoices and filter by client, number, date or status.",
      "Open the correct sales invoice and check the lines, total and amount due.",
      "Use its PDF or print preview when available.",
      "Check the recorded status before arranging any follow-up."
    ],
    "category": "Money",
    "notice": "Bills and purchase orders have separate read permissions. Viewing an invoice does not allow editing or sending it."
  },
  {
    "id": "A09",
    "areas": [
      "admin"
    ],
    "title": "Read money received and receipt details",
    "entryPath": "/admin/money/bank",
    "contexts": [
      {
        "path": "/admin/money/bank",
        "tab": "in"
      },
      {
        "path": "/admin/money/bank",
        "tab": "allocated"
      }
    ],
    "requirements": [
      "bank.manage"
    ],
    "steps": [
      "Open Bank and choose Unallocated (or Allocated payments).",
      "Filter the incoming payment list.",
      "Read the payment reference, note and existing invoice links.",
      "Open the available receipt details; check the current allocation before making changes."
    ],
    "category": "Money"
  },
  {
    "id": "A10",
    "areas": [
      "admin"
    ],
    "title": "Read payments out",
    "entryPath": "/admin/money/bank",
    "contexts": [
      {
        "path": "/admin/money/bank",
        "tab": "out"
      }
    ],
    "requirements": [
      "bank.manage"
    ],
    "steps": [
      "Open Bank and choose Payments out.",
      "Find the relevant supplier payment.",
      "Read the available details and links."
    ],
    "category": "Money",
    "superadminOnly": true,
    "notice": "Payments out is restricted to super admins. Ordinary bank access does not enable it."
  },
  {
    "id": "A11",
    "areas": [
      "admin"
    ],
    "title": "Review a person's account and access",
    "entryPath": "/admin/users",
    "contexts": [
      {
        "path": "/admin/users"
      },
      {
        "path": "/admin/users/:id"
      }
    ],
    "requirements": [
      "users.view"
    ],
    "steps": [
      "Find the person under Users and open their account.",
      "Read the account status, areas and roles.",
      "Review the manager modules, staff modules and assigned sites shown for that person.",
      "Ask an authorised access administrator to make any required change."
    ],
    "category": "People and access"
  },
  {
    "id": "A12",
    "areas": [
      "admin"
    ],
    "title": "Use HR self-service",
    "entryPath": "/admin/hr",
    "contexts": [
      {
        "path": "/admin/hr"
      }
    ],
    "requirements": [
      "hr.self"
    ],
    "steps": [
      "Open HR to see your own holiday and requests.",
      "Choose Holiday, Sickness, Overtime or Expenses as appropriate.",
      "Review the dates and details before saving a request.",
      "Read its recorded status and any response."
    ],
    "category": "People and access",
    "notice": "Hours worked is a separate opt-in. Its guide appears only when enabled for your account."
  },
  {
    "id": "A13",
    "areas": [
      "admin"
    ],
    "title": "Follow the NHBC upload queue",
    "entryPath": "/admin/nhbc",
    "contexts": [
      {
        "path": "/admin/nhbc"
      }
    ],
    "requirements": [
      "nhbc.manage"
    ],
    "steps": [
      "Open NHBC and find the relevant upload.",
      "Read its current status and checkpoint.",
      "Check any hold or failure detail before taking an available action.",
      "A deployment may briefly pause and resume the consumer; use the current queue status."
    ],
    "category": "Evidence",
    "notice": "Use the queue's current consumer state. This guide does not start, stop or retry work, and does not replace the normal operator checks."
  },
  {
    "id": "A14",
    "areas": [
      "admin"
    ],
    "title": "Open a SAP project and its reports",
    "entryPath": "/admin/sap/app",
    "contexts": [
      {
        "path": "/admin/sap"
      },
      {
        "path": "/admin/sap/app"
      },
      {
        "path": "/admin/sap/app/:id"
      },
      {
        "path": "/admin/sap/plots/:id"
      },
      {
        "path": "/admin/sap/window-report/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "sap"
      }
    ],
    "requirements": [
      "sap.view"
    ],
    "steps": [
      "Open Kaizen SAP and choose the required project or dwelling.",
      "Use the project navigation to find its inputs and results.",
      "Open the available report for that dwelling or site.",
      "Use your technical procedure before editing, calculating or submitting work."
    ],
    "category": "Specialist tools",
    "notice": "SAP editing needs its separate permission. This guide explains buttons and navigation, not regulatory or calculation procedure."
  },
  {
    "id": "task-create",
    "areas": [
      "admin"
    ],
    "title": "Create a manual task",
    "entryPath": "/admin/tasks",
    "contexts": [
      {
        "path": "/admin/tasks"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "tasks"
      }
    ],
    "requirements": [
      "tasks.view",
      [
        "tasks.create",
        "tasks.manage"
      ]
    ],
    "steps": [
      "Open Tasks or the project's Tasks tab and choose New task.",
      "Enter a clear title and choose the project.",
      "Set the due date, priority, estimate and assignees as appropriate.",
      "Review and create once; open the new task to check it."
    ],
    "category": "Tasks",
    "actionMode": "editable",
    "notice": "Creating a task does not grant task-management rights. Existing access and assignee restrictions still apply."
  },
  {
    "id": "project-create",
    "areas": [
      "admin"
    ],
    "title": "Create a project and linked site",
    "entryPath": "/admin/projects",
    "contexts": [
      {
        "path": "/admin/projects"
      },
      {
        "path": "/admin/companies/:id"
      }
    ],
    "requirements": [
      "projects.view",
      "projects.edit"
    ],
    "steps": [
      "Open Projects and choose New project, or start from the client company.",
      "Choose the existing client company, or use Add a new company when appropriate.",
      "Enter the project name, optional reference and site address; indicate SAP work if applicable.",
      "Choose Create project once, then add the project's tasks."
    ],
    "category": "Projects",
    "actionMode": "editable"
  },
  {
    "id": "invoice-create",
    "areas": [
      "admin"
    ],
    "title": "Create a sales invoice",
    "entryPath": "/admin/invoices/new",
    "contexts": [
      {
        "path": "/admin/invoices/new"
      },
      {
        "path": "/admin/invoices"
      }
    ],
    "requirements": [
      "invoices.view",
      "invoices.manage"
    ],
    "steps": [
      "Open Invoices and choose New invoice.",
      "Select the client and linked project, then enter the invoice dates and lines.",
      "Review quantities, prices, tax, address and payment terms.",
      "Save and inspect the invoice preview before sending it."
    ],
    "category": "Money",
    "actionMode": "editable",
    "notice": "Saving an invoice and emailing it are separate actions. This does not create a bill or purchase order."
  },
  {
    "id": "invoice-send",
    "areas": [
      "admin"
    ],
    "title": "Email a sales invoice",
    "entryPath": "/admin/invoices",
    "contexts": [
      {
        "path": "/admin/invoices/:id"
      }
    ],
    "requirements": [
      "invoices.view",
      "invoices.manage"
    ],
    "steps": [
      "Open the sales invoice and check the recipient, lines and PDF preview.",
      "Choose Send and review the recipient list and message.",
      "Confirm once and read the recorded result.",
      "Check Email log if you also have email-history access; do not resend an uncertain result blindly."
    ],
    "category": "Money",
    "actionMode": "live",
    "notice": "Sending email and posting to Xero are separate. Accepted means submitted for delivery, not confirmed delivered. Purchase documents cannot use this sales send flow."
  },
  {
    "id": "email-history",
    "areas": [
      "admin"
    ],
    "title": "Read the notification and email history",
    "entryPath": "/admin/emails",
    "contexts": [
      {
        "path": "/admin/emails"
      },
      {
        "path": "/admin/site-emails/:id"
      }
    ],
    "requirements": [
      "emails.view"
    ],
    "steps": [
      "Open Email log and filter by recipient, date, type or status.",
      "Open the relevant message to inspect saved content and attachments.",
      "Read the delivery attempts and current result.",
      "Distinguish accepted, delivered, bounced, suppressed and unknown before arranging follow-up."
    ],
    "category": "People and access",
    "keywords": [
      "notification",
      "mail",
      "SES",
      "send history"
    ],
    "notice": "Older logs can contain metadata only. Do not assume every historic message has saved content or a confirmed delivery receipt."
  },
  {
    "id": "role-duplicate",
    "areas": [
      "admin"
    ],
    "title": "Duplicate a staff role",
    "entryPath": "/admin/roles",
    "contexts": [
      {
        "path": "/admin/roles"
      },
      {
        "path": "/admin/users"
      }
    ],
    "requirements": [
      "users.view",
      "roles.manage"
    ],
    "steps": [
      "Open Users, then Roles.",
      "Choose Duplicate role on the role you want to copy.",
      "Give the new role a distinct name and review its permissions and two-step sign-in setting.",
      "Save the separate role. Assigning it to a person is a separate action."
    ],
    "category": "People and access",
    "notice": "Duplicating a role assigns nobody automatically. Review permissions before assigning the new role."
  },
  {
    "id": "manager-modules",
    "areas": [
      "admin"
    ],
    "title": "Change a manager's available modules",
    "entryPath": "/admin/users",
    "contexts": [
      {
        "path": "/admin/users/:id"
      }
    ],
    "requirements": [
      "users.view",
      "users.manage"
    ],
    "steps": [
      "Open the person under Users.",
      "In Manager portal modules, review the current on/off choices.",
      "Change only the intended modules and choose Save modules.",
      "The manager's menu updates after reloading. Site and plot assignments are managed separately."
    ],
    "category": "People and access",
    "actionMode": "live"
  },
  {
    "id": "staff-modules",
    "areas": [
      "admin"
    ],
    "title": "Enable staff Operations or Hours worked",
    "entryPath": "/admin/users",
    "contexts": [
      {
        "path": "/admin/users/:id"
      }
    ],
    "requirements": [
      "users.view",
      "roles.manage"
    ],
    "steps": [
      "Open the staff member under Users.",
      "Find Staff modules and review Operations and Hours worked.",
      "Enable only the intended tool and choose Save staff modules.",
      "The person still needs the normal task or HR permission for its actions."
    ],
    "category": "People and access",
    "actionMode": "live",
    "notice": "These are explicit opt-ins. A role name alone is not proof that either tool is enabled."
  },
  {
    "id": "bills-read",
    "areas": [
      "admin"
    ],
    "title": "Read a bill or purchase reference",
    "entryPath": "/admin/money/bills",
    "contexts": [
      {
        "path": "/admin/money/bills"
      }
    ],
    "requirements": [
      [
        "bills.view",
        "bills.manage"
      ]
    ],
    "steps": [
      "Open Bills and find the supplier document.",
      "Read its amount, status, available file and links.",
      "Use the independent bill permissions for purchase references, including those with historical invoice URLs."
    ],
    "category": "Money",
    "notice": "Invoice-view permission does not confer bill access. Reading a purchase reference does not permit sales-document actions."
  },
  {
    "id": "purchase-orders-read",
    "areas": [
      "admin"
    ],
    "title": "Purchase orders: projects, quotes and PDFs",
    "entryPath": "/admin/money/purchase-orders",
    "contexts": [
      {
        "path": "/admin/money/purchase-orders"
      },
      {
        "path": "/admin/money/purchase-orders/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "purchaseorders"
      },
      {
        "path": "/admin/quotes/:id"
      }
    ],
    "requirements": [
      [
        "purchaseorders.view",
        "purchaseorders.manage"
      ]
    ],
    "steps": [
      "Find a client PO under Money › Purchase orders, or on a project under Money › Purchase orders, which lists the project’s POs and the client’s POs that cover all its projects. Each row shows Value (net), Invoiced, Remaining, the linked quote and the PDF.",
      "On a quote, the Purchase orders section shows the POs raised against it and its PO cover (its open POs against the quote value). Use Add PO to log a new one with the client, project, quote and value filled in, or Link an existing PO to attach one of the client’s POs. A PO number the client typed when approving the quote can be turned into a record with Create PO record from this.",
      "Open a PO to see its quote, the invoices counted against it and its value history. A PO made when a client approved a quote online is marked “Check before invoicing”: compare it with the client’s PO, then choose Value is right or Change value.",
      "Use Attach PDF on the PO’s page to save the client’s PO document, or Replace PDF to swap it. Some PDFs from the old hub can’t be opened here; attach the PDF again to fix them.",
      "Keep supplier bills and supplier payments separate; these screens track client POs."
    ],
    "category": "Money",
    "notice": "The same PO number can’t be logged twice for one client, and a PO can only be linked to that client’s quotes."
  },
  {
    "id": "hr-hours",
    "areas": [
      "admin"
    ],
    "title": "Enter your hours worked",
    "entryPath": "/admin/hr",
    "contexts": [
      {
        "path": "/admin/hr",
        "tab": "hours"
      }
    ],
    "requirements": [
      "hr.self"
    ],
    "steps": [
      "Open HR and choose Hours worked.",
      "Check the displayed pay period and your details.",
      "Enter your own hours and any required notes.",
      "Save and check the total shown."
    ],
    "category": "People and access",
    "staffModule": "hrHours",
    "actionMode": "editable",
    "notice": "Hours worked must be enabled for your staff account. This guide does not enable it."
  },
  {
    "id": "hr-team-hours",
    "areas": [
      "admin"
    ],
    "title": "Review team hours",
    "entryPath": "/admin/hr/manage",
    "contexts": [
      {
        "path": "/admin/hr/manage",
        "tab": "hours"
      }
    ],
    "requirements": [
      "hr.manage"
    ],
    "steps": [
      "Open HR admin and choose Hours worked.",
      "Check the selected pay period.",
      "Review the people, days, hours and totals.",
      "Follow up discrepancies through the existing HR process."
    ],
    "category": "People and access",
    "staffModule": "hrHours"
  },
  {
    "id": "quotes-read",
    "areas": [
      "admin"
    ],
    "title": "Find and review a quote",
    "entryPath": "/admin/quotes",
    "contexts": [
      {
        "path": "/admin/quotes"
      },
      {
        "path": "/admin/quotes/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "quotes"
      }
    ],
    "requirements": [
      "quotes.view"
    ],
    "steps": [
      "Open Quotes and find the correct quote.",
      "Open its details to read the client, lines and current status.",
      "Use the available preview to inspect the document.",
      "Editing or sending requires the separate quote-management permission."
    ],
    "category": "Sales"
  },
  {
    "id": "contacts-managers",
    "areas": [
      "admin"
    ],
    "title": "Find a client manager or contact",
    "entryPath": "/admin/contacts",
    "contexts": [
      {
        "path": "/admin/contacts"
      },
      {
        "path": "/admin/companies/:id"
      }
    ],
    "requirements": [
      "projects.view"
    ],
    "steps": [
      "Open Contacts and select the client-manager directory when needed.",
      "Search by name, email or company.",
      "Read the contact details, linked companies and assigned-project count.",
      "Use View user access only if that control is available to your account."
    ],
    "category": "Projects",
    "keywords": [
      "contact",
      "manager",
      "phone",
      "email",
      "client lookup"
    ],
    "notice": "Directory access does not give permission to edit a person's account or their assigned sites."
  },
  {
    "id": "bank-note",
    "areas": [
      "admin"
    ],
    "title": "Add or edit an incoming payment note",
    "entryPath": "/admin/money/bank",
    "contexts": [
      {
        "path": "/admin/money/bank",
        "tab": "in"
      },
      {
        "path": "/admin/money/bank",
        "tab": "allocated"
      }
    ],
    "requirements": [
      "bank.manage"
    ],
    "steps": [
      "Open Bank > Unallocated (or Allocated payments) and find the correct transaction.",
      "Choose Add note or View/Edit note.",
      "Enter the note and choose Save note.",
      "If saving cannot be confirmed, close the note and refresh before editing again."
    ],
    "category": "Money",
    "actionMode": "editable",
    "notice": "A note does not allocate the payment or change its amount. Payments out remains restricted to super admins."
  },
  {
    "id": "siteplans-navigation",
    "areas": [
      "admin"
    ],
    "title": "Open a site plan",
    "entryPath": "/admin/siteplans",
    "contexts": [
      {
        "path": "/admin/siteplans"
      },
      {
        "path": "/admin/siteplans/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "siteplans"
      }
    ],
    "requirements": [
      "siteplans.view"
    ],
    "steps": [
      "Open Site plans and select the required plan.",
      "Use the viewer to find the plot or drawing.",
      "Editing, analysis and applying values require their own permissions."
    ],
    "category": "Specialist tools",
    "notice": "This guide covers the tool's buttons and navigation. Technical and regulatory procedure remains separate."
  },
  {
    "id": "models-navigation",
    "areas": [
      "admin"
    ],
    "title": "Find drawings and models",
    "entryPath": "/admin/models",
    "contexts": [
      {
        "path": "/admin/models"
      },
      {
        "path": "/admin/models/:id"
      },
      {
        "path": "/admin/projects/:id",
        "tab": "models"
      }
    ],
    "requirements": [
      "models.view"
    ],
    "steps": [
      "Open SketchUp models and choose an available model.",
      "Use the viewer or linked project context.",
      "Uploading or choosing a model for a plot requires separate management permission."
    ],
    "category": "Specialist tools",
    "notice": "This guide covers the tool's buttons and navigation. Technical and regulatory procedure remains separate."
  },
  {
    "id": "wlc-navigation",
    "areas": [
      "admin"
    ],
    "title": "Open Whole Life Carbon work",
    "entryPath": "/admin/wlc",
    "contexts": [
      {
        "path": "/admin/wlc"
      },
      {
        "path": "/admin/wlc/:id"
      }
    ],
    "requirements": [
      "wlc.use"
    ],
    "steps": [
      "Open Whole life carbon and choose the project.",
      "Find its inputs and report views.",
      "Follow the approved technical method before changing calculations."
    ],
    "category": "Specialist tools",
    "notice": "This guide covers the tool's buttons and navigation. Technical and regulatory procedure remains separate."
  },
  {
    "id": "grid-navigation",
    "areas": [
      "admin"
    ],
    "title": "Open Grid Capacity work",
    "entryPath": "/admin/grid",
    "contexts": [
      {
        "path": "/admin/grid"
      },
      {
        "path": "/admin/grid/:id"
      }
    ],
    "requirements": [
      "grid.use"
    ],
    "steps": [
      "Open Grid capacity and choose the project.",
      "Read its inputs and results.",
      "Follow the approved technical method for design decisions."
    ],
    "category": "Specialist tools",
    "notice": "This guide covers the tool's buttons and navigation. Technical and regulatory procedure remains separate."
  },
  {
    "id": "statements-navigation",
    "areas": [
      "admin"
    ],
    "title": "Find an energy statement",
    "entryPath": "/admin/statements",
    "contexts": [
      {
        "path": "/admin/statements"
      },
      {
        "path": "/admin/statements/:id"
      }
    ],
    "requirements": [
      "statements.use"
    ],
    "steps": [
      "Open Statements and choose the project.",
      "Read the statement inputs and report.",
      "Planning policy changes need the separate planning-policy permission."
    ],
    "category": "Specialist tools",
    "notice": "This guide covers the tool's buttons and navigation. Technical and regulatory procedure remains separate."
  }
]
